import "@/App.css";
import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import { PublicLayout } from "@/components/public/PublicLayout";
import { DriftLayout, MontorLayout } from "@/components/drift/DriftLayout";
import Home from "@/pages/public/Home";
import MarketPage from "@/pages/public/MarketPage";
import Projects from "@/pages/public/Projects";
import ProjectCase from "@/pages/public/ProjectCase";
import About from "@/pages/public/About";
import Careers from "@/pages/public/Careers";
import Contact from "@/pages/public/Contact";
import Prices from "@/pages/public/Prices";
import Login from "@/pages/drift/Login";
import Dashboard from "@/pages/drift/Dashboard";
import Inquiries from "@/pages/drift/Inquiries";
import Customers from "@/pages/drift/Customers";
import CustomerDetail from "@/pages/drift/CustomerDetail";
import LocationDetail from "@/pages/drift/LocationDetail";
import Orders from "@/pages/drift/Orders";
import OrderDetail from "@/pages/drift/OrderDetail";
import Planning from "@/pages/drift/Planning";
import Quotes from "@/pages/drift/Quotes";
import InvoiceBasis from "@/pages/drift/InvoiceBasis";
import AiWorkspace from "@/pages/drift/AiWorkspace";
import Felt from "@/pages/drift/Felt";
import MontorHome from "@/pages/drift/MontorHome";
import MontorOrder from "@/pages/drift/MontorOrder";

function RequireAuth({ office = false }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="min-h-screen bg-background" />;
  if (!user) return <Navigate to="/drift/login" replace state={{ from: location.pathname }} />;
  if (office && user.role === "montor") return <Navigate to="/drift/montor" replace />;
  return <Outlet />;
}

function NotFound() {
  return (
    <div className="container-public flex min-h-[60vh] flex-col items-start justify-center py-24">
      <p className="text-[13.5px] font-medium text-muted-foreground">Feil 404</p>
      <h1 className="t-h1 mt-3">Sida finst ikkje.</h1>
      <a href="/" className="u-link mt-6 text-[15px] font-medium" style={{ textDecorationColor: "currentColor" }}>Til framsida</a>
    </div>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route element={<PublicLayout />}>
              <Route path="/" element={<Home />} />
              <Route path="/industri" element={<MarketPage slug="industri" />} />
              <Route path="/samferdsel" element={<MarketPage slug="samferdsel" />} />
              <Route path="/bygg-naering" element={<MarketPage slug="bygg-naering" />} />
              <Route path="/service-bustad" element={<MarketPage slug="service-bustad" />} />
              <Route path="/prisar" element={<Prices />} />
              <Route path="/prosjekt" element={<Projects />} />
              <Route path="/prosjekt/:slug" element={<ProjectCase />} />
              <Route path="/om-oss" element={<About />} />
              <Route path="/karriere" element={<Careers />} />
              <Route path="/kontakt" element={<Contact />} />
              <Route path="*" element={<NotFound />} />
            </Route>

            {/* Same front page, in the colours and with the logo of the current kvinnheradelektro.no */}
            <Route element={<PublicLayout variant="gammel" />}>
              <Route path="/gammel" element={<Home />} />
            </Route>

            <Route path="/drift/login" element={<Login />} />

            <Route element={<RequireAuth office />}>
              <Route element={<DriftLayout />}>
                <Route path="/drift" element={<Dashboard />} />
                <Route path="/drift/henvendingar" element={<Inquiries />} />
                <Route path="/drift/henvendingar/:id" element={<Inquiries />} />
                <Route path="/drift/kundar" element={<Customers />} />
                <Route path="/drift/kundar/:id" element={<CustomerDetail />} />
                <Route path="/drift/anlegg/:id" element={<LocationDetail />} />
                <Route path="/drift/oppdrag" element={<Orders />} />
                <Route path="/drift/oppdrag/:id" element={<OrderDetail />} />
                <Route path="/drift/planlegging" element={<Planning />} />
                <Route path="/drift/tilbod" element={<Quotes />} />
                <Route path="/drift/fakturagrunnlag" element={<InvoiceBasis />} />
                <Route path="/drift/ai" element={<AiWorkspace />} />
                <Route path="/drift/ai/:id" element={<AiWorkspace />} />
                <Route path="/drift/felt" element={<Felt />} />
              </Route>
            </Route>

            <Route element={<RequireAuth />}>
              <Route element={<MontorLayout />}>
                <Route path="/drift/montor" element={<MontorHome />} />
                <Route path="/drift/montor/oppdrag/:id" element={<MontorOrder />} />
              </Route>
            </Route>
          </Routes>
          <Toaster position="top-right" closeButton />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
